import React, { useEffect } from 'react';
import { AlertCircle, XCircle, CheckCircle, Info, X } from 'lucide-react';

const ValidationMessage = ({
  message,
  type = 'error', // 'error', 'warning', 'success', 'info'
  onClose,
  autoClose = false,
  autoCloseDelay = 5000,
  position = 'top', // 'top', 'bottom', 'center'
  className = ''
}) => {
  useEffect(() => {
    if (autoClose && onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDelay, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-azure-500" />;
    }
  };

  const getBackgroundClass = () => {
    switch (type) {
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'success':
        return 'bg-emerald-50 border-emerald-200';
      case 'info':
      default:
        return 'bg-azure-50 border-azure-200';
    }
  };

  const getTextClass = () => {
    switch (type) {
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-amber-800';
      case 'success':
        return 'text-emerald-800';
      case 'info':
      default:
        return 'text-azure-800';
    }
  };

  const getPositionClass = () => {
    switch (position) {
      case 'bottom':
        return 'fixed bottom-4 left-1/2 transform -translate-x-1/2';
      case 'center':
        return 'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2';
      case 'top':
      default:
        return 'fixed top-4 left-1/2 transform -translate-x-1/2';
    }
  };

  if (!message) return null;

  return (
    <div 
      className={`${getPositionClass()} z-50 max-w-lg w-full mx-4 ${className}`}
      role="alert"
    >
      <div className={`flex items-start gap-3 p-4 rounded-xl border ${getBackgroundClass()} shadow-lg`}>
        <div className="flex-shrink-0 mt-0.5">
          {getIcon()}
        </div>
        <div className={`flex-1 text-sm font-medium ${getTextClass()}`}>
          {message}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className={`flex-shrink-0 p-1 rounded-lg hover:bg-white/50 transition-colors ${getTextClass()}`}
            aria-label="Close message"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Hook for managing validation messages
export const useValidationMessage = () => {
  const [validationState, setValidationState] = React.useState({
    message: '',
    type: 'error',
    isVisible: false
  });

  const showValidation = (message, type = 'error') => {
    setValidationState({
      message,
      type,
      isVisible: true
    });
  };

  const hideValidation = () => {
    setValidationState(prev => ({ ...prev, isVisible: false }));
  };

  const ValidationComponent = ({ ...props }) => {
    if (!validationState.isVisible) return null;
    
    return (
      <ValidationMessage
        message={validationState.message}
        type={validationState.type}
        onClose={hideValidation}
        {...props}
      />
    );
  };

  return {
    showValidation,
    hideValidation,
    ValidationComponent
  };
};

export default ValidationMessage;